import { useCallback, useEffect, useRef, useState } from 'react';
import { type AgentPolicy } from '@repo/api/schemas/agentPolicy.schema';

import type { PolicyFilterValues } from './PolicyPageFilter/PolicyPageFilter';

import { PolicyPageFilter } from './PolicyPageFilter';
import { PolicyPageHeader } from './PolicyPageHeader';
import { PolicyPageSidePanel } from './PolicyPageSidePanel';
import { PolicyPageTable } from './PolicyPageTable';

import './PoliciesPage.css';

const CONTENT_ARIA = 'Content';

/**
 * AI Agent Policies (Rules) page.
 *
 * Renders the page header, filter bar, policy table, and a slide-in
 * {@link PolicyPageSidePanel} for the selected policy. The panel pushes the
 * page content via a margin transition while it slides in from the right.
 */
export default function PoliciesPage() {
  const [filterValues, setFilterValues] = useState<PolicyFilterValues>({
    searchValue: '',
    selectedFilters: {},
  });
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState<AgentPolicy | null>(
    null
  );
  const pageRowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const probe = document.createElement('div');
    probe.style.cssText =
      'position:absolute;top:-9999px;width:100px;height:100px;overflow:scroll;visibility:hidden;';
    document.body.appendChild(probe);
    const scrollbarWidth = probe.offsetWidth - probe.clientWidth;
    document.body.removeChild(probe);
    pageRowRef.current?.style.setProperty(
      '--agent-scrollbar-width',
      `${scrollbarWidth}px`
    );
  }, []);

  useEffect(() => {
    const node = pageRowRef.current;
    if (!node) {
      return;
    }
    const setWidthVar = (px: number) => {
      node.style.setProperty('--agent-page-row-width', `${px}px`);
    };
    setWidthVar(node.getBoundingClientRect().width);
    let initial = true;
    let settleTimer: number | undefined;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setWidthVar(entry.contentRect.width);
      }
      if (initial) {
        initial = false;
        return;
      }
      node.style.setProperty('--agent-page-transition-duration', '0s');
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => {
        node.style.removeProperty('--agent-page-transition-duration');
      }, 80);
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
      window.clearTimeout(settleTimer);
    };
  }, []);

  const handleRowClick = useCallback((policy: AgentPolicy) => {
    setSelectedPolicy(policy);
    setIsSidePanelOpen(true);
  }, []);

  const handleSidePanelClose = useCallback(() => {
    setIsSidePanelOpen(false);
    setSelectedPolicy(null);
  }, []);

  const handlePolicyUpdate = useCallback((data: Partial<AgentPolicy>) => {
    setSelectedPolicy((prev) => (prev ? { ...prev, ...data } : prev));
  }, []);

  return (
    <div className='Agent-PoliciesPage-pageRow' ref={pageRowRef}>
      <div
        className={`Agent-PoliciesPage-page${
          isSidePanelOpen ? ' Agent-PoliciesPage-page--isPanelOpen' : ''
        }`}>
        <PolicyPageHeader />

        <div
          className={`Agent-PoliciesPage-viewControls${
            isSidePanelOpen
              ? ' Agent-PoliciesPage-viewControls--isPanelOpen'
              : ''
          }`}>
          <PolicyPageFilter onFilterChange={setFilterValues} />
        </div>

        <section
          className={`Agent-PoliciesPage-body${
            isSidePanelOpen ? ' Agent-PoliciesPage-body--isPanelOpen' : ''
          }`}
          aria-label={CONTENT_ARIA}>
          <PolicyPageTable
            filterValues={filterValues}
            selectedPolicy={selectedPolicy}
            onRowClick={handleRowClick}
            onPolicyUpdate={handlePolicyUpdate}
            setIsSidePanelOpen={setIsSidePanelOpen}
          />
        </section>
      </div>

      <div
        className={`Agent-PoliciesPage-sidePanelWrapper${
          isSidePanelOpen ? ' Agent-PoliciesPage-sidePanelWrapper--isOpen' : ''
        }`}
        data-open={isSidePanelOpen}>
        {isSidePanelOpen && selectedPolicy && (
          <PolicyPageSidePanel
            policy={selectedPolicy}
            isOpen={isSidePanelOpen}
            onClose={handleSidePanelClose}
            onPolicyUpdate={handlePolicyUpdate}
          />
        )}
      </div>
    </div>
  );
}
